import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ErrorMessage, PageLoader, Spinner } from '@/components/ui'
import { api } from '@/lib/api'
import {
  COLUMN_TYPE_TITLES,
  READINESS_TITLES,
  type MappingItem,
  type MappingState,
  type ReadinessName,
  type RoleInfo,
  type TemplateAvailability,
} from '@/lib/types'
import { useActiveWorkspace, useHasPermission } from '@/stores/auth'

/** `null` в semantic_role означает «пропустить колонку». */
const SKIP_VALUE = '__skip__'

/**
 * Экран подтверждения смысла колонок.
 *
 * Ключевое требование: пользователь не должен заполнять двадцать колонок
 * вручную. Система приходит с готовыми предложениями (`auto`), человек их
 * проверяет. Справа сразу видно, какие отчёты станут доступны и чего для
 * остальных не хватает — иначе непонятно, зачем эта работа вообще нужна.
 */
export function MappingPage() {
  const { versionId } = useParams<{ versionId: string }>()
  const workspace = useActiveWorkspace()
  const canEdit = useHasPermission('dataset:mapping_edit')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const mapping = useQuery({
    queryKey: ['mapping', versionId],
    queryFn: () => api.get<MappingState>(`/dataset-versions/${versionId}/mapping`),
    enabled: Boolean(versionId) && Boolean(workspace),
  })

  /** Выбор пользователя: column_index → роль либо SKIP_VALUE. */
  const [choices, setChoices] = useState<Record<number, string>>({})
  const [isDirty, setIsDirty] = useState(false)

  // Состояние формы инициализируется ответом сервера один раз: последующие
  // перезагрузки не должны затирать несохранённый выбор пользователя
  useEffect(() => {
    if (!mapping.data || isDirty) return
    const initial: Record<number, string> = {}
    for (const item of mapping.data.items) {
      initial[item.column_index] = item.semantic_role ?? SKIP_VALUE
    }
    setChoices(initial)
  }, [mapping.data, isDirty])

  const confirm = useMutation({
    mutationFn: (assignments: { column_index: number; semantic_role: string | null }[]) =>
      api.put<MappingState>(`/dataset-versions/${versionId}/mapping`, { assignments }),
    onSuccess: (state) => {
      queryClient.setQueryData(['mapping', versionId], state)
      void queryClient.invalidateQueries({ queryKey: ['dataset-version', versionId] })
      void queryClient.invalidateQueries({ queryKey: ['datasets'] })
      setIsDirty(false)
    },
  })

  const roleIndex = useMemo(() => {
    const index = new Map<string, RoleInfo>()
    for (const role of mapping.data?.roles ?? []) index.set(role.role, role)
    return index
  }, [mapping.data])

  /** Роли, помеченные как unique, нельзя назначить дважды. */
  const usedUniqueRoles = useMemo(() => {
    const counts = new Map<string, number>()
    for (const value of Object.values(choices)) {
      if (value === SKIP_VALUE) continue
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
    return counts
  }, [choices])

  const duplicateRoles = useMemo(
    () =>
      [...usedUniqueRoles.entries()]
        .filter(([role, count]) => count > 1 && roleIndex.get(role)?.unique)
        .map(([role]) => roleIndex.get(role)?.title ?? role),
    [usedUniqueRoles, roleIndex],
  )

  if (!workspace || mapping.isLoading) return <PageLoader label="Готовим сопоставление…" />

  if (mapping.error) {
    return (
      <div className="space-y-4">
        <ErrorMessage error={mapping.error} />
        <Link to="/sources" className="btn-secondary">
          Вернуться к источникам
        </Link>
      </div>
    )
  }

  const data = mapping.data
  if (!data) return null

  function handleSubmit(): void {
    const assignments = data!.items.map((item) => {
      const value = choices[item.column_index] ?? SKIP_VALUE
      return {
        column_index: item.column_index,
        semantic_role: value === SKIP_VALUE ? null : value,
      }
    })
    confirm.mutate(assignments)
  }

  const autoCount = data.items.filter((item) => item.source === 'auto').length
  const assignedCount = Object.values(choices).filter((value) => value !== SKIP_VALUE).length

  return (
    <div className="space-y-6">
      <section className="card p-6">
        <h2 className="text-base font-semibold text-slate-900">Что означают ваши колонки</h2>
        {/* После подтверждения все назначения становятся выбором пользователя,
            и счётчик автораспознанных колонок обнуляется. Показывать «распознали
            0 из 8» на подтверждённом сопоставлении — врать пользователю. */}
        <p className="mt-1 text-sm text-slate-500">
          {data.confirmed
            ? 'Смысл колонок задан. Его можно изменить и сохранить заново — отчёты пересчитаются по новым правилам.'
            : `Мы распознали ${autoCount} из ${data.items.length} колонок автоматически. Проверьте предложения и подтвердите — после этого можно строить отчёты.`}
        </p>

        {data.confirmed && (
          <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            Сопоставление подтверждено, данные подготовлены для отчётов
            {data.projection_status === 'ready' ? '' : ' (проекция ещё строится)'}.
          </p>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <section className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-2.5 font-medium">Колонка в файле</th>
                  <th className="px-3 py-2.5 font-medium">Тип</th>
                  <th className="px-6 py-2.5 font-medium">Смысл</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((item) => (
                  <MappingRow
                    key={item.column_index}
                    item={item}
                    roleIndex={roleIndex}
                    value={choices[item.column_index] ?? SKIP_VALUE}
                    disabled={!canEdit || confirm.isPending}
                    onChange={(value) => {
                      setIsDirty(true)
                      setChoices((current) => ({ ...current, [item.column_index]: value }))
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <footer className="flex flex-wrap items-center gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
            {canEdit ? (
              <>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={confirm.isPending || duplicateRoles.length > 0 || assignedCount === 0}
                  onClick={handleSubmit}
                >
                  {confirm.isPending && <Spinner className="h-4 w-4" />}
                  {data.confirmed ? 'Сохранить изменения' : 'Подтвердить сопоставление'}
                </button>
                <span className="text-xs text-slate-500">
                  Назначено роли: {assignedCount} из {data.items.length}
                </span>
              </>
            ) : (
              <span className="text-sm text-slate-500">
                У вашей роли нет прав менять сопоставление — только просмотр.
              </span>
            )}
            {data.confirmed && !isDirty && (
              <button
                type="button"
                className="btn-secondary ml-auto"
                onClick={() => void navigate(`/dataset-versions/${data.dataset_version_id}`)}
              >
                К профилю данных
              </button>
            )}
          </footer>

          {duplicateRoles.length > 0 && (
            <p className="border-t border-red-200 bg-red-50 px-6 py-3 text-sm text-red-800">
              Роль можно назначить только одной колонке: {duplicateRoles.join(', ')}.
            </p>
          )}

          <div className="px-6 pb-4">
            <ErrorMessage error={confirm.error} />
          </div>
        </section>

        <aside className="space-y-4">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Какие отчёты доступны
          </h3>
          {data.templates.map((template) => (
            <TemplateCard key={template.code} template={template} />
          ))}
        </aside>
      </div>

      <Link to={`/dataset-versions/${data.dataset_version_id}`} className="btn-ghost">
        ← Профиль данных
      </Link>
    </div>
  )
}

function MappingRow({
  item,
  roleIndex,
  value,
  disabled,
  onChange,
}: {
  item: MappingItem
  roleIndex: Map<string, RoleInfo>
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const selectedRole = value === SKIP_VALUE ? null : roleIndex.get(value)

  return (
    <tr>
      <td className="px-6 py-3">
        <p className="font-medium text-slate-900">{item.source_name}</p>
        {item.source === 'auto' && item.reason && (
          <p className="mt-0.5 text-xs text-slate-500">
            Предложено автоматически: {item.reason}
            {item.confidence > 0 && ` (уверенность ${Math.round(item.confidence * 100)}%)`}
          </p>
        )}
        {/* Роль со `source === 'user'` могла быть и выбрана руками, и просто
            принята из автопредложения при подтверждении: backend в обоих
            случаях фиксирует выбор пользователя. Поэтому «Подтверждено», а не
            «Выбрано вручную» — иначе подпись противоречит тому, что человек
            делал на самом деле. */}
        {item.source === 'user' && <p className="mt-0.5 text-xs text-brand-700">Подтверждено</p>}
      </td>
      <td className="px-3 py-3 text-slate-500">{COLUMN_TYPE_TITLES[item.column_type]}</td>
      <td className="px-6 py-3">
        {/* Список ограничен allowed_roles: предлагать «выручку» для текстовой
            колонки — значит гарантировать ошибку при построении проекции */}
        <select
          className="input py-1.5 text-sm"
          value={value}
          disabled={disabled}
          aria-label={`Смысл колонки «${item.source_name}»`}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value={SKIP_VALUE}>— не использовать —</option>
          {item.allowed_roles.map((role) => (
            <option key={role} value={role}>
              {roleIndex.get(role)?.title ?? role}
            </option>
          ))}
        </select>
        {selectedRole?.is_personal_data && (
          <p className="mt-1 text-xs text-amber-700">
            Персональные данные: значения не попадут в AI-запросы.
          </p>
        )}
      </td>
    </tr>
  )
}

function TemplateCard({ template }: { template: TemplateAvailability }) {
  const styles: Record<ReadinessName, string> = {
    ready: 'border-emerald-200 bg-emerald-50',
    partial: 'border-amber-200 bg-amber-50',
    insufficient_data: 'border-slate-200 bg-slate-50',
  }
  const badges: Record<ReadinessName, string> = {
    ready: 'bg-emerald-100 text-emerald-800',
    partial: 'bg-amber-100 text-amber-800',
    insufficient_data: 'bg-slate-200 text-slate-700',
  }

  const unavailable = template.widgets.filter((widget) => !widget.available)

  return (
    <div className={`rounded-xl border p-4 ${styles[template.readiness]}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900">{template.title}</p>
        <span className={`badge shrink-0 ${badges[template.readiness]}`}>
          {READINESS_TITLES[template.readiness]}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-600">{template.description}</p>
      <p className="mt-2 text-xs text-slate-500">
        Виджетов доступно: {template.available_widget_count} из {template.widgets.length}
      </p>

      {/* Честный ответ «чего не хватает» вместо пустого графика (ТЗ §2.4).
          Для полностью недоступного шаблона это его обязательные поля, для
          частично доступного — поля отдельных виджетов: шаблон бывает
          «в целом доступен», а половина виджетов в нём — нет. */}
      {template.missing_titles.length > 0 && (
        <div className="mt-2 border-t border-white/60 pt-2">
          <p className="text-xs font-medium text-slate-700">
            Не хватает: {template.missing_titles.join(', ')}
          </p>
          <ul className="mt-1 space-y-0.5">
            {template.how_to_obtain.map((hint) => (
              <li key={hint} className="text-xs text-slate-500">
                • {hint}
              </li>
            ))}
          </ul>
        </div>
      )}

      {unavailable.length > 0 && (
        <details className="mt-2 border-t border-white/60 pt-2">
          <summary className="cursor-pointer text-xs font-medium text-slate-700">
            Недоступно виджетов: {unavailable.length}
          </summary>
          <ul className="mt-1.5 space-y-1.5">
            {unavailable.map((widget) => (
              <li key={widget.title} className="text-xs text-slate-600">
                <span className="font-medium">{widget.title}</span> — нужны:{' '}
                {widget.missing_titles.join(', ')}
                {widget.how_to_obtain.length > 0 && (
                  <span className="block text-slate-500">{widget.how_to_obtain.join(' ')}</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
